import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, ShoppingCart, Calendar, LogOut, Search, Lock,
  Plus, Edit2, Trash2, Save, X, MapPin, Package, Boxes, Minus, ChevronLeft, ChevronRight,
} from "lucide-react";
import PortalHero from "../components/portal/PortalHero";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "heritage-manager-token";
const NAME_KEY = "heritage-manager-name";

const mgrApi = axios.create({ baseURL: API });
mgrApi.interceptors.request.use((c) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const TABS = [
  { v: "dashboard", label: "Dashboard", Icon: LayoutDashboard },
  { v: "inventory", label: "Inventory", Icon: Boxes },
  { v: "orders", label: "Order log", Icon: ShoppingCart },
  { v: "customers", label: "Customer DB", Icon: Users },
  { v: "calendar", label: "Calendar", Icon: Calendar },
  { v: "routes", label: "Delivery routes", Icon: MapPin },
  { v: "subscriptions", label: "Subscriptions", Icon: Package },
];

/* =================== Login (Heritage palette) =================== */

export function ManagementLogin() {
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/manager/login`, form);
      localStorage.setItem(TOKEN_KEY, r.data.token);
      localStorage.setItem(NAME_KEY, r.data.name || "");
      toast.success("Welcome.");
      nav("/management");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Login failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="manager-login"
      className="min-h-[100svh] bg-[var(--dahlias-cream)] text-[var(--dahlias-ink)] flex items-center justify-center p-5"
    >
      <div className="w-full max-w-md bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/30 wobble-a p-8 md:p-10">
        <Link to="/" className="block text-center mb-4">
          <img src="/photos/brand/heritage-seal.png" alt="Heritage Dahlias" className="w-24 h-24 mx-auto" />
        </Link>
        <div className="flex items-center justify-center gap-2 text-[var(--dahlias-clay)]">
          <Lock className="w-3.5 h-3.5" />
          <span className="text-[10px] font-label tracking-[0.4em] uppercase">Heritage HQ</span>
        </div>
        <h1 className="mt-3 font-display text-3xl text-[var(--dahlias-forest)] text-center">
          Management <span className="font-display-italic text-[var(--dahlias-clay)]">portal</span>
        </h1>
        <p className="mt-1 text-sm text-[var(--dahlias-ink)]/65 text-center">Internal access only.</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <label className="block">
            <span className="block text-[10px] font-label tracking-[0.3em] uppercase text-[var(--dahlias-sage-deep)] mb-1">Email</span>
            <input
              required type="email" data-testid="manager-login-email"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full h-11 px-4 bg-white border border-[var(--dahlias-sage)]/30 rounded-full text-sm focus:outline-none focus:border-[var(--dahlias-sage-deep)]"
            />
          </label>
          <label className="block">
            <span className="block text-[10px] font-label tracking-[0.3em] uppercase text-[var(--dahlias-sage-deep)] mb-1">Password</span>
            <input
              required type="password" data-testid="manager-login-password"
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full h-11 px-4 bg-white border border-[var(--dahlias-sage)]/30 rounded-full text-sm focus:outline-none focus:border-[var(--dahlias-sage-deep)]"
            />
          </label>
          <button
            type="submit" disabled={busy} data-testid="manager-login-submit"
            className="w-full h-12 bg-[var(--dahlias-forest)] hover:bg-[var(--dahlias-sage-deep)] text-[var(--dahlias-cream)] disabled:opacity-50 rounded-full font-medium tracking-wide text-sm"
          >
            {busy ? "Signing in…" : "Enter HQ"}
          </button>
        </form>

        <Link to="/" className="block mt-6 text-center text-xs font-label tracking-[0.3em] uppercase text-[var(--dahlias-ink)]/50 hover:text-[var(--dahlias-clay)]">
          ← Back to Heritage
        </Link>
      </div>
    </div>
  );
}

/* =================== Shell + tabs =================== */

export default function ManagementPortal() {
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) { nav("/management/login"); return; }
    (async () => {
      try { const r = await mgrApi.get("/manager/me"); setMe(r.data); }
      catch (e) { localStorage.removeItem(TOKEN_KEY); nav("/management/login"); }
      finally { setLoading(false); }
    })();
  }, [nav]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(NAME_KEY);
    nav("/management/login");
  };

  if (loading) return <div className="min-h-[100svh] grid place-items-center text-[var(--dahlias-ink)]/55 bg-[var(--dahlias-cream)]">Loading HQ…</div>;
  if (!me) return null;

  return (
    <div data-testid="management-portal" className="min-h-[100svh] bg-[var(--dahlias-cream)] text-[var(--dahlias-ink)] font-sans">
      <PortalHero
        kicker="✦ Heritage · Operations ✦"
        title={
          <>
            Heritage{" "}
            <span className="font-display-italic text-[#F4C8D2]">Operations.</span>
          </>
        }
        subtitle="Inventory management and operations"
        nav={TABS.map(({ v, label, Icon }) => ({
          label,
          Icon,
          active: tab === v,
          onClick: (e) => { e.preventDefault(); setTab(v); },
          to: "#",
          testid: `manager-tab-${v}`,
        }))}
        userName={me.name || me.email}
        onLogout={logout}
      />

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-12">
        {tab === "dashboard" && <DashboardPanel />}
        {tab === "inventory" && <InventoryPanel />}
        {tab === "orders" && <OrdersPanel />}
        {tab === "customers" && <CustomersPanel />}
        {tab === "calendar" && <CalendarPanel />}
        {tab === "routes" && <RoutesPanel />}
        {tab === "subscriptions" && <SubscriptionsPanel />}
      </main>
    </div>
  );
}

/* =================== UI atoms =================== */

function Stat({ label, value, sub, tone = "default" }) {
  const tones = {
    default: "bg-[var(--dahlias-paper)] border-[var(--dahlias-sage)]/25",
    sage:    "bg-[var(--dahlias-sage)]/12 border-[var(--dahlias-sage-deep)]/40",
    clay:    "bg-[var(--dahlias-clay)]/12 border-[var(--dahlias-clay)]/40",
    warn:    "bg-[var(--dahlias-rust)]/12 border-[var(--dahlias-rust)]/40",
  };
  return (
    <div className={`wobble-a p-5 border-2 ${tones[tone] || tones.default}`}>
      <div className="text-[10px] font-label tracking-[0.3em] uppercase text-[var(--dahlias-sage-deep)]">{label}</div>
      <div className="mt-2 font-display text-3xl text-[var(--dahlias-forest)]">{value}</div>
      {sub && <div className="mt-1 text-xs text-[var(--dahlias-ink)]/65">{sub}</div>}
    </div>
  );
}

function Table({ headers, children, testid }) {
  return (
    <div className="bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/25 wobble-a overflow-x-auto">
      <table className="w-full text-sm" data-testid={testid}>
        <thead className="text-[var(--dahlias-sage-deep)] text-[10px] tracking-[0.25em] uppercase bg-[var(--dahlias-sage)]/10">
          <tr>{headers.map((h, i) => <th key={h.key || h.label || `col-${i}`} className={`px-4 py-3 ${h.align === "right" ? "text-right" : "text-left"}`}>{h.label}</th>)}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

/* =================== Dashboard =================== */

function DashboardPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const r = await mgrApi.get("/manager/dashboard"); setData(r.data); }
      catch (e) { toast.error("Dashboard load failed."); }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <div className="text-[var(--dahlias-ink)]/55">Loading…</div>;
  if (!data) return null;
  return (
    <div className="space-y-7" data-testid="manager-dashboard">
      <div>
        <span className="font-label tracking-[0.4em] text-[11px] text-[var(--dahlias-clay)] uppercase">✦ Operational overview ✦</span>
        <h2 className="mt-1 font-display text-4xl text-[var(--dahlias-forest)]">High-level health</h2>
        <p className="text-sm text-[var(--dahlias-ink)]/65">As of {new Date(data.as_of).toLocaleString("en-NZ")}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Unfulfilled orders" value={data.orders.unfulfilled} sub="Need action" tone="clay" />
        <Stat label="Low stock SKUs" value={data.inventory.low_stock} sub={`${data.inventory.out_of_stock} out`} tone={data.inventory.low_stock + data.inventory.out_of_stock > 0 ? "warn" : "default"} />
        <Stat label="Jobs this week" value={data.jobs_this_week} sub="Calendar tab" tone="sage" />
        <Stat label="Active routes" value={data.routes.active} sub={`${data.routes.total} total`} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Combined orders" value={data.orders.combined_total} sub="Logged to HQ" />
        <Stat label="Inventory units" value={data.inventory.total_units} sub={`${data.inventory.total_skus} SKUs`} />
        <Stat label="Customer accounts" value={data.customers.with_accounts} sub="Portal sign-ups" />
        <Stat label="Interest list" value={data.interest_list} sub="Awaiting follow-up" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Bloom Club subs" value={data.bloom_club.subscribers} />
        <Stat label="Bloom Club waitlist" value={data.bloom_club.waitlist} />
        <Stat label="Growers, active" value={data.growers.active} />
        <Stat label="Growers, pending" value={data.growers.pending} tone={data.growers.pending > 0 ? "clay" : "default"} />
      </div>
    </div>
  );
}

/* =================== Inventory =================== */

function InventoryPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlyLow, setOnlyLow] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await mgrApi.get("/manager/inventory", { params: { only_low: onlyLow } });
      setRows(r.data || []);
    } catch (e) { toast.error("Inventory load failed."); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const r = await mgrApi.get("/manager/inventory", { params: { only_low: onlyLow } });
        if (mounted) setRows(r.data || []);
      } catch { if (mounted) toast.error("Inventory load failed."); }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [onlyLow]);

  const adjust = async (productId, variantId, delta) => {
    try {
      await mgrApi.patch(`/manager/inventory/${productId}/variants/${variantId}/stock`, { delta });
      await load();
    } catch (e) { toast.error("Adjustment failed."); }
  };

  return (
    <div className="space-y-5" data-testid="manager-inventory">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="font-label tracking-[0.4em] text-[11px] text-[var(--dahlias-clay)] uppercase">✦ Inventory ✦</span>
          <h2 className="mt-1 font-display text-4xl text-[var(--dahlias-forest)]">Stock on hand</h2>
          <p className="text-sm text-[var(--dahlias-ink)]/65">{rows.length} products. Adjust stock with the +/− buttons.</p>
        </div>
        <label className="inline-flex items-center gap-2 px-4 h-10 wobble-pill border border-[var(--dahlias-sage)]/30 bg-[var(--dahlias-paper)] cursor-pointer text-sm">
          <input type="checkbox" checked={onlyLow} onChange={(e) => setOnlyLow(e.target.checked)} data-testid="inventory-low-toggle" />
          Show only low/out
        </label>
      </div>

      {loading ? <div className="text-[var(--dahlias-ink)]/55">Loading…</div> : (
        <div className="space-y-4">
          {rows.map((p) => (
            <article key={p.id} data-testid={`inventory-product-${p.id}`} className="bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/25 wobble-a p-5">
              <div className="flex items-center justify-between gap-3 mb-3">
                <h3 className="font-display text-xl text-[var(--dahlias-forest)]">{p.name}</h3>
                <div className="flex items-center gap-2">
                  {p.any_out && <span className="px-2 py-0.5 wobble-pill text-[10px] tracking-[0.25em] uppercase bg-[var(--dahlias-rust)]/15 text-[var(--dahlias-rust)]">Out</span>}
                  {p.any_low && <span className="px-2 py-0.5 wobble-pill text-[10px] tracking-[0.25em] uppercase bg-[var(--dahlias-clay)]/15 text-[var(--dahlias-clay)]">Low</span>}
                  <span className="text-xs text-[var(--dahlias-ink)]/55 capitalize">{p.category}</span>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {p.variants.map((v) => (
                  <div key={v.id} data-testid={`inventory-variant-${v.id}`} className="flex items-center justify-between bg-[var(--dahlias-cream)] border border-[var(--dahlias-sage)]/20 p-3 wobble-a">
                    <div className="min-w-0">
                      <div className="text-sm text-[var(--dahlias-forest)] font-display truncate">{v.label}</div>
                      {v.sku && <div className="text-[10px] font-mono text-[var(--dahlias-ink)]/45">{v.sku}</div>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => adjust(p.id, v.id, -1)} className="w-8 h-8 wobble-blob bg-[var(--dahlias-cream)] border border-[var(--dahlias-sage)]/30 hover:bg-[var(--dahlias-clay)]/15 grid place-items-center" aria-label="Decrease">
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className={`font-display text-xl min-w-[2ch] text-center ${v.stock === 0 ? "text-[var(--dahlias-rust)]" : v.stock <= 5 ? "text-[var(--dahlias-clay)]" : "text-[var(--dahlias-forest)]"}`}>
                        {v.stock}
                      </span>
                      <button onClick={() => adjust(p.id, v.id, 1)} className="w-8 h-8 wobble-blob bg-[var(--dahlias-sage)]/15 border border-[var(--dahlias-sage)]/30 hover:bg-[var(--dahlias-sage)]/30 grid place-items-center" aria-label="Increase">
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </article>
          ))}
          {rows.length === 0 && <div className="bg-[var(--dahlias-paper)] border-2 border-dashed border-[var(--dahlias-sage)]/30 wobble-a p-10 text-center text-[var(--dahlias-ink)]/55 italic">No products to show.</div>}
        </div>
      )}
    </div>
  );
}

/* =================== Orders =================== */

function OrdersPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const r = await mgrApi.get("/manager/orders"); setRows(r.data || []); }
      catch (e) { toast.error("Orders load failed."); }
      finally { setLoading(false); }
    })();
  }, []);
  return (
    <div className="space-y-5" data-testid="manager-orders">
      <div>
        <span className="font-label tracking-[0.4em] text-[11px] text-[var(--dahlias-clay)] uppercase">✦ Order Log ✦</span>
        <h2 className="mt-1 font-display text-4xl text-[var(--dahlias-forest)]">Combined order log</h2>
        <p className="text-sm text-[var(--dahlias-ink)]/65">{rows.length} most recent across all channels.</p>
      </div>
      {loading ? <div className="text-[var(--dahlias-ink)]/55">Loading…</div> : (
        <Table headers={[
          { label: "Date" }, { label: "Channel" }, { label: "Customer" },
          { label: "Items" }, { label: "Status" }, { label: "Paid" },
        ]}>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-[var(--dahlias-sage)]/15 hover:bg-[var(--dahlias-sage)]/5">
              <td className="px-4 py-2.5 text-[var(--dahlias-ink)]/70 text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString("en-NZ") : "-"}</td>
              <td className="px-4 py-2.5"><span className={`px-2 py-0.5 wobble-pill text-[10px] tracking-[0.2em] uppercase ${r.source === "online" ? "bg-[var(--dahlias-clay)]/15 text-[var(--dahlias-clay)]" : "bg-[var(--dahlias-sage)]/15 text-[var(--dahlias-sage-deep)]"}`}>{r.source}</span></td>
              <td className="px-4 py-2.5 text-[var(--dahlias-forest)]">{r.customer_name || r.email || "-"}</td>
              <td className="px-4 py-2.5 text-[var(--dahlias-ink)]/70 text-sm">{r.items_count || 0}</td>
              <td className="px-4 py-2.5 text-[var(--dahlias-ink)]/65 text-xs capitalize">{r.status || "-"}</td>
              <td className="px-4 py-2.5"><span className={`px-2 py-0.5 wobble-pill text-[10px] tracking-[0.2em] uppercase ${r.paid ? "bg-[var(--dahlias-sage-deep)] text-white" : "bg-[var(--dahlias-clay)]/15 text-[var(--dahlias-clay)]"}`}>{r.paid ? "Paid" : "Awaiting"}</span></td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

/* =================== Customers =================== */

function CustomersPanel() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try { const r = await mgrApi.get("/manager/customers"); if (mounted) setRows(r.data || []); }
      catch { if (mounted) toast.error("Customers load failed."); }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, []);
  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase(); if (!n) return rows;
    return rows.filter((c) => [c.email, c.name, c.phone].join(" ").toLowerCase().includes(n));
  }, [rows, q]);

  const openCustomer = async (em) => {
    setLoadingDetail(true);
    setDetail({ email: em, loading: true });
    try {
      const r = await mgrApi.get(`/manager/customers/${encodeURIComponent(em)}`);
      setDetail(r.data);
    } catch (e) {
      toast.error("Couldn't load customer details.");
      setDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="manager-customers">
      <div>
        <span className="font-label tracking-[0.4em] text-[11px] text-[var(--dahlias-clay)] uppercase">✦ Customer Database ✦</span>
        <h2 className="mt-1 font-display text-4xl text-[var(--dahlias-forest)]">All customers</h2>
        <p className="text-sm text-[var(--dahlias-ink)]/65">{rows.length} unique. Click a row to view full details.</p>
      </div>
      <div className="relative max-w-md">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--dahlias-ink)]/40" />
        <input
          value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid="manager-customer-search"
          className="w-full h-11 pl-10 pr-4 bg-[var(--dahlias-paper)] border border-[var(--dahlias-sage)]/30 rounded-full text-sm focus:outline-none focus:border-[var(--dahlias-sage-deep)]"
        />
      </div>
      {loading ? <div className="text-[var(--dahlias-ink)]/55">Loading…</div> : (
        <Table headers={[
          { label: "Customer" }, { label: "Email" }, { label: "Orders", align: "right" },
          { label: "Last order" }, { label: "Sources" },
        ]}>
          {filtered.slice(0, 200).map((c, i) => (
            <tr
              key={c.email || i}
              onClick={() => c.email && openCustomer(c.email)}
              data-testid={`customer-row-${c.email || i}`}
              className={`border-t border-[var(--dahlias-sage)]/15 transition ${c.email ? "cursor-pointer hover:bg-[var(--dahlias-sage)]/10" : ""}`}
            >
              <td className="px-4 py-2.5 text-[var(--dahlias-forest)]">{c.name || "-"}{c.has_account && <span className="ml-2 text-[10px] tracking-[0.25em] uppercase text-[var(--dahlias-clay)]">✦ ACCOUNT</span>}</td>
              <td className="px-4 py-2.5 text-[var(--dahlias-ink)]/70 font-mono text-xs">{c.email || "-"}</td>
              <td className="px-4 py-2.5 text-right text-[var(--dahlias-forest)] font-display">{c.order_count}</td>
              <td className="px-4 py-2.5 text-[var(--dahlias-ink)]/65 text-xs">{c.last_order_at ? new Date(c.last_order_at).toLocaleDateString("en-NZ") : "-"}</td>
              <td className="px-4 py-2.5 text-[var(--dahlias-ink)]/55 text-xs">{(c.sources || []).join(", ")}</td>
            </tr>
          ))}
        </Table>
      )}

      {detail && (
        <CustomerDetailDrawer
          detail={detail}
          loading={loadingDetail}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}

function CustomerDetailDrawer({ detail, loading, onClose }) {
  return (
    <div data-testid="customer-detail-drawer" className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center md:justify-end" onClick={onClose}>
      <div className="bg-[var(--dahlias-paper)] w-full md:max-w-2xl md:h-full overflow-y-auto p-6 md:p-7 border-t-2 md:border-l-2 md:border-t-0 border-[var(--dahlias-sage)]/30" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-3xl text-[var(--dahlias-forest)]">Customer</h3>
          <button onClick={onClose} className="p-2 wobble-blob bg-[var(--dahlias-cream)] hover:bg-[var(--dahlias-sage)]/10" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
        {loading || detail.loading ? <div className="text-[var(--dahlias-ink)]/55">Loading…</div> : (
          <div className="space-y-5">
            <header className="bg-[var(--dahlias-cream)] border border-[var(--dahlias-sage)]/25 wobble-a p-5">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="font-display text-2xl text-[var(--dahlias-forest)]">{detail.name || "-"}</h2>
                {detail.has_account && <span className="px-2 py-0.5 wobble-pill text-[10px] tracking-[0.25em] uppercase bg-[var(--dahlias-clay)]/15 text-[var(--dahlias-clay)]">✦ Heritage Account</span>}
              </div>
              <div className="grid sm:grid-cols-2 gap-2 mt-3 text-sm">
                <DetailRow label="Email" value={<a href={`mailto:${detail.email}`} className="font-mono text-[var(--dahlias-clay)] underline">{detail.email}</a>} />
                <DetailRow label="Phone" value={detail.phone || "-"} />
                <DetailRow label="Order count" value={detail.order_count} />
                <DetailRow label="Joined portal" value={detail.account_created_at ? new Date(detail.account_created_at).toLocaleDateString("en-NZ") : "-"} />
              </div>
              {detail.address && (
                <div className="mt-3 text-sm">
                  <DetailRow label="Address" value={<span className="whitespace-pre-wrap">{typeof detail.address === "string" ? detail.address : JSON.stringify(detail.address)}</span>} />
                </div>
              )}
            </header>

            {detail.bloom_club_subscriber && (
              <section className="bg-[var(--dahlias-sage)]/12 border border-[var(--dahlias-sage-deep)]/30 wobble-a p-4">
                <span className="font-label tracking-[0.3em] text-[10px] uppercase text-[var(--dahlias-sage-deep)]">✦ Bloom Club subscriber ✦</span>
                <div className="mt-1 text-sm">
                  <strong>{[detail.bloom_club_subscriber.size, detail.bloom_club_subscriber.vase, detail.bloom_club_subscriber.frequency].filter(Boolean).join(" · ") || "Active"}</strong>
                  {detail.bloom_club_subscriber.region && <span className="text-[var(--dahlias-ink)]/65"> · {detail.bloom_club_subscriber.region}</span>}
                </div>
              </section>
            )}

            {detail.bloom_club_waitlist && !detail.bloom_club_subscriber && (
              <section className="bg-[var(--dahlias-clay)]/12 border border-[var(--dahlias-clay)]/30 wobble-a p-4">
                <span className="font-label tracking-[0.3em] text-[10px] uppercase text-[var(--dahlias-clay)]">✦ On Bloom Club waitlist ✦</span>
                <div className="mt-1 text-sm text-[var(--dahlias-ink)]/80">
                  {detail.bloom_club_waitlist.region}{detail.bloom_club_waitlist.postcode ? ` · ${detail.bloom_club_waitlist.postcode}` : ""}
                </div>
              </section>
            )}

            <section>
              <h4 className="font-display text-xl text-[var(--dahlias-forest)] mb-2">All orders ({detail.order_count})</h4>
              {detail.order_count === 0 ? (
                <p className="text-sm text-[var(--dahlias-ink)]/55 italic">No orders on file.</p>
              ) : (
                <div className="space-y-2">
                  {[...(detail.online_orders || []).map(o => ({ ...o, _src: "online" })),
                    ...(detail.manual_orders || []).map(o => ({ ...o, _src: "manual" }))]
                    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
                    .map((o) => (
                    <article key={o.id} data-testid={`detail-order-${o.id}`} className="bg-[var(--dahlias-cream)] border border-[var(--dahlias-sage)]/25 wobble-a p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--dahlias-sage-deep)]">
                            {o.created_at ? new Date(o.created_at).toLocaleDateString("en-NZ") : "-"} · {o._src}
                          </div>
                          <div className="font-display text-base text-[var(--dahlias-forest)]">
                            {o.order_number || `${o.items_count || (o.line_items || []).length || (o.items ? 1 : 0)} items`}
                          </div>
                        </div>
                        <span className={`px-2 py-0.5 wobble-pill text-[10px] tracking-[0.25em] uppercase ${o.paid || o.payment_status === "paid" ? "bg-[var(--dahlias-sage-deep)] text-white" : "bg-[var(--dahlias-clay)]/15 text-[var(--dahlias-clay)]"}`}>
                          {o.paid || o.payment_status === "paid" ? "Paid" : "Awaiting"}
                        </span>
                      </div>
                      {o.items && <pre className="mt-2 whitespace-pre-wrap text-xs text-[var(--dahlias-ink)]/85 font-sans">{o.items}</pre>}
                      {Array.isArray(o.line_items) && o.line_items.length > 0 && (
                        <ul className="mt-2 text-xs text-[var(--dahlias-ink)]/80 space-y-0.5">
                          {o.line_items.map((li, i) => <li key={li.id || li.sku || `${li.name || "li"}-${i}`}>{li.qty}× {li.name}{li.variant_label ? `, ${li.variant_label}` : ""}</li>)}
                        </ul>
                      )}
                      {o.notes && <p className="mt-1.5 italic text-[var(--dahlias-ink)]/65 text-xs">&ldquo;{o.notes}&rdquo;</p>}
                      {o.tracking_number && <div className="mt-1 text-[10px] font-mono text-[var(--dahlias-ink)]/55">Tracking: {o.tracking_number}</div>}
                    </article>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-label tracking-[0.3em] uppercase text-[var(--dahlias-sage-deep)]">{label}</div>
      <div className="text-[var(--dahlias-forest)]">{value}</div>
    </div>
  );
}

/* =================== Calendar / Jobs =================== */

function CalendarPanel() {
  const [refDate, setRefDate] = useState(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - d.getDay()); return d;
  });
  const [jobs, setJobs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(refDate); d.setDate(refDate.getDate() + i); return d;
    });
  }, [refDate]);

  const weekStart = weekDays[0].toISOString();
  const weekEnd = new Date(weekDays[6].getTime() + 86400000).toISOString();

  const load = async () => {
    setLoading(true);
    try {
      const r = await mgrApi.get("/manager/jobs", { params: { start: weekStart, end: weekEnd } });
      setJobs(r.data || []);
    } catch (e) { toast.error("Jobs load failed."); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const r = await mgrApi.get("/manager/jobs", { params: { start: weekStart, end: weekEnd } });
        if (mounted) setJobs(r.data || []);
      } catch { if (mounted) toast.error("Jobs load failed."); }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [weekStart, weekEnd]);

  const jobsByDay = useMemo(() => {
    const map = {};
    weekDays.forEach((d) => { map[d.toDateString()] = []; });
    jobs.forEach((j) => {
      const d = new Date(j.starts_at).toDateString();
      if (map[d]) map[d].push(j);
    });
    return map;
  }, [jobs, weekDays]);

  const shiftWeek = (dir) => {
    const d = new Date(refDate); d.setDate(refDate.getDate() + dir * 7); setRefDate(d);
  };

  const blank = () => ({
    id: null, title: "", description: "", starts_at: new Date().toISOString().slice(0, 16),
    ends_at: "", location: "", assignee: "", job_type: "general", status: "scheduled",
  });

  const saveJob = async (j) => {
    if (!j.title.trim()) { toast.error("Title required."); return; }
    const payload = {
      title: j.title.trim(), description: j.description || "",
      starts_at: new Date(j.starts_at).toISOString(),
      ends_at: j.ends_at ? new Date(j.ends_at).toISOString() : null,
      location: j.location || "", assignee: j.assignee || "",
      job_type: j.job_type || "general", status: j.status || "scheduled",
    };
    try {
      if (j.id) await mgrApi.patch(`/manager/jobs/${j.id}`, payload);
      else await mgrApi.post("/manager/jobs", payload);
      toast.success("Job saved.");
      setEditing(null); await load();
    } catch (e) { toast.error("Save failed."); }
  };

  const deleteJob = async (id) => {
    if (!window.confirm("Delete this job?")) return;
    try { await mgrApi.delete(`/manager/jobs/${id}`); setEditing(null); await load(); toast.success("Deleted."); }
    catch (e) { toast.error("Delete failed."); }
  };

  const typeColours = {
    general: "bg-[var(--dahlias-sage)]/20 text-[var(--dahlias-sage-deep)] border-[var(--dahlias-sage)]/40",
    dispatch: "bg-[var(--dahlias-clay)]/20 text-[var(--dahlias-clay)] border-[var(--dahlias-clay)]/40",
    growing: "bg-emerald-700/15 text-emerald-800 border-emerald-700/30",
    admin: "bg-[var(--dahlias-paper)] text-[var(--dahlias-ink)]/80 border-[var(--dahlias-sage)]/30",
    delivery: "bg-[var(--dahlias-rust)]/15 text-[var(--dahlias-rust)] border-[var(--dahlias-rust)]/30",
  };

  return (
    <div className="space-y-5" data-testid="manager-calendar">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="font-label tracking-[0.4em] text-[11px] text-[var(--dahlias-clay)] uppercase">✦ Work Week ✦</span>
          <h2 className="mt-1 font-display text-4xl text-[var(--dahlias-forest)]">Calendar</h2>
          <p className="text-sm text-[var(--dahlias-ink)]/65">Schedule jobs for the team. Click a day to add, or a job to edit.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => shiftWeek(-1)} data-testid="cal-prev-week" className="w-9 h-9 wobble-blob border border-[var(--dahlias-sage)]/30 grid place-items-center hover:bg-[var(--dahlias-sage)]/10"><ChevronLeft className="w-4 h-4" /></button>
          <div className="px-4 h-9 grid place-items-center wobble-pill border border-[var(--dahlias-sage)]/30 text-sm">
            {weekDays[0].toLocaleDateString("en-NZ", { day: "numeric", month: "short" })}, {weekDays[6].toLocaleDateString("en-NZ", { day: "numeric", month: "short" })}
          </div>
          <button onClick={() => shiftWeek(1)} data-testid="cal-next-week" className="w-9 h-9 wobble-blob border border-[var(--dahlias-sage)]/30 grid place-items-center hover:bg-[var(--dahlias-sage)]/10"><ChevronRight className="w-4 h-4" /></button>
          <button onClick={() => setEditing(blank())} data-testid="cal-new-job" className="ml-2 inline-flex items-center gap-1.5 h-9 px-4 wobble-pill bg-[var(--dahlias-forest)] hover:bg-[var(--dahlias-sage-deep)] text-[var(--dahlias-cream)] text-sm">
            <Plus className="w-4 h-4" /> New job
          </button>
        </div>
      </div>

      {loading ? <div className="text-[var(--dahlias-ink)]/55">Loading…</div> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-7 gap-3">
          {weekDays.map((d) => {
            const key = d.toDateString();
            const list = jobsByDay[key] || [];
            const isToday = key === new Date().toDateString();
            return (
              <div key={key} data-testid={`cal-day-${d.toISOString().slice(0,10)}`} className={`bg-[var(--dahlias-paper)] border-2 wobble-a p-3 min-h-[160px] ${isToday ? "border-[var(--dahlias-clay)]" : "border-[var(--dahlias-sage)]/25"}`}>
                <button
                  className="w-full text-left mb-2"
                  onClick={() => {
                    const start = new Date(d); start.setHours(9, 0, 0, 0);
                    setEditing({ ...blank(), starts_at: toLocalInput(start) });
                  }}
                >
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--dahlias-sage-deep)]">
                    {d.toLocaleDateString("en-NZ", { weekday: "short" })}
                  </div>
                  <div className="font-display text-2xl text-[var(--dahlias-forest)]">{d.getDate()}</div>
                </button>
                <div className="space-y-1.5">
                  {list.map((j) => (
                    <button
                      key={j.id} onClick={() => setEditing({ ...j, starts_at: toLocalInput(new Date(j.starts_at)), ends_at: j.ends_at ? toLocalInput(new Date(j.ends_at)) : "" })}
                      data-testid={`cal-job-${j.id}`}
                      className={`w-full text-left p-2 wobble-a border ${typeColours[j.job_type] || typeColours.general} hover:translate-y-[-1px] transition`}
                    >
                      <div className="font-display text-sm leading-tight">{j.title}</div>
                      <div className="text-[10px] opacity-75">{new Date(j.starts_at).toLocaleTimeString("en-NZ", { hour: "numeric", minute: "2-digit" })}{j.assignee ? ` · ${j.assignee}` : ""}</div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <JobEditor j={editing} onChange={setEditing} onSave={() => saveJob(editing)} onDelete={() => editing.id && deleteJob(editing.id)} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

function toLocalInput(d) {
  const pad = (n) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function JobEditor({ j, onChange, onSave, onDelete, onClose }) {
  return (
    <div data-testid="job-editor" className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-5" onClick={onClose}>
      <div className="bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-clay)] wobble-a max-w-lg w-full p-6 shadow-2xl space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl text-[var(--dahlias-forest)]">{j.id ? "Edit job" : "New job"}</h3>
          <button onClick={onClose} className="p-1"><X className="w-5 h-5" /></button>
        </div>
        <Field label="Title *">
          <input value={j.title} onChange={(e) => onChange({ ...j, title: e.target.value })} data-testid="job-title" className="job-input" placeholder="e.g. Dispatch Mystery Box orders" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Starts *"><input type="datetime-local" value={j.starts_at} onChange={(e) => onChange({ ...j, starts_at: e.target.value })} data-testid="job-starts" className="job-input" /></Field>
          <Field label="Ends"><input type="datetime-local" value={j.ends_at} onChange={(e) => onChange({ ...j, ends_at: e.target.value })} className="job-input" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Assignee"><input value={j.assignee} onChange={(e) => onChange({ ...j, assignee: e.target.value })} className="job-input" placeholder="Jess" /></Field>
          <Field label="Type">
            <select value={j.job_type} onChange={(e) => onChange({ ...j, job_type: e.target.value })} className="job-input">
              <option value="general">General</option>
              <option value="dispatch">Dispatch</option>
              <option value="growing">Growing</option>
              <option value="admin">Admin</option>
              <option value="delivery">Delivery</option>
            </select>
          </Field>
        </div>
        <Field label="Location"><input value={j.location} onChange={(e) => onChange({ ...j, location: e.target.value })} className="job-input" placeholder="The farm / customer address" /></Field>
        <Field label="Notes"><textarea value={j.description} onChange={(e) => onChange({ ...j, description: e.target.value })} rows={3} className="job-input min-h-[80px]" /></Field>
        <Field label="Status">
          <select value={j.status} onChange={(e) => onChange({ ...j, status: e.target.value })} className="job-input">
            <option value="scheduled">Scheduled</option>
            <option value="in_progress">In progress</option>
            <option value="complete">Complete</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </Field>
        <div className="flex justify-between pt-2">
          {j.id ? (
            <button onClick={onDelete} data-testid="job-delete" className="inline-flex items-center gap-1.5 h-10 px-4 wobble-pill bg-[var(--dahlias-rust)]/15 hover:bg-[var(--dahlias-rust)]/25 text-[var(--dahlias-rust)] text-sm">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          ) : <span />}
          <button onClick={onSave} data-testid="job-save" className="inline-flex items-center gap-1.5 h-10 px-5 wobble-pill bg-[var(--dahlias-forest)] hover:bg-[var(--dahlias-sage-deep)] text-[var(--dahlias-cream)] text-sm">
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
        <style>{`.job-input{width:100%;height:42px;padding:0 14px;border-radius:999px;background:white;border:1px solid color-mix(in srgb, var(--dahlias-sage) 30%, transparent);font-size:14px;font-family:inherit;}.job-input:focus{outline:none;border-color:var(--dahlias-sage-deep);}textarea.job-input{padding:10px 14px;border-radius:18px;height:auto;}`}</style>
      </div>
    </div>
  );
}

/* =================== Delivery routes =================== */

function buildMapsUrl(stops) {
  const addrs = (stops || [])
    .map((s) => (s.address || "").trim())
    .filter(Boolean)
    .map(encodeURIComponent);
  if (addrs.length === 0) return null;
  if (addrs.length === 1) {
    return `https://www.google.com/maps/search/?api=1&query=${addrs[0]}`;
  }
  // Google Maps directions with multi-stop: origin / destination / waypoints
  return `https://www.google.com/maps/dir/${addrs.join("/")}`;
}

function formatStopAddress(addr) {
  if (!addr) return "";
  if (typeof addr === "string") return addr;
  return [addr.line1, addr.line2, addr.suburb, addr.city, addr.postcode]
    .filter(Boolean)
    .join(", ");
}

function RoutesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [eligibleOrders, setEligibleOrders] = useState([]);
  const [showPicker, setShowPicker] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await mgrApi.get("/manager/routes"); setRows(r.data || []); }
    catch (e) { toast.error("Routes load failed."); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try { const r = await mgrApi.get("/manager/routes"); if (mounted) setRows(r.data || []); }
      catch { if (mounted) toast.error("Routes load failed."); }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, []);

  // Load HQ orders for the picker when the editor opens
  useEffect(() => {
    if (!editing) { setShowPicker(false); return; }
    (async () => {
      try {
        const r = await mgrApi.get("/manager/orders");
        setEligibleOrders(r.data || []);
      } catch {
        setEligibleOrders([]);
      }
    })();
  }, [editing]);

  const blank = () => ({ id: null, name: "", region: "", notes: "", stops: [], active: true, scheduled_for: "" });

  const save = async () => {
    if (!editing.name.trim()) { toast.error("Name required."); return; }
    const payload = {
      name: editing.name.trim(), region: editing.region || "",
      notes: editing.notes || "",
      stops: editing.stops || [], active: !!editing.active,
      scheduled_for: editing.scheduled_for || null,
    };
    try {
      if (editing.id) await mgrApi.patch(`/manager/routes/${editing.id}`, payload);
      else await mgrApi.post("/manager/routes", payload);
      toast.success("Route saved."); setEditing(null); await load();
    } catch (e) { toast.error("Save failed."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this route?")) return;
    try { await mgrApi.delete(`/manager/routes/${id}`); await load(); toast.success("Deleted."); }
    catch (e) { toast.error("Delete failed."); }
  };

  const updateStop = (idx, patch) => {
    const next = [...(editing.stops || [])]; next[idx] = { ...next[idx], ...patch }; setEditing({ ...editing, stops: next });
  };
  const removeStop = (idx) => {
    const next = [...(editing.stops || [])]; next.splice(idx, 1); setEditing({ ...editing, stops: next });
  };
  const addStop = () => setEditing({ ...editing, stops: [...(editing.stops || []), { label: "", address: "", status: "pending" }] });

  const moveStop = (idx, dir) => {
    const next = [...(editing.stops || [])];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setEditing({ ...editing, stops: next });
  };

  const addOrderAsStop = (o) => {
    const addr = formatStopAddress(o.address);
    if (!addr) {
      toast.error("That order has no address.");
      return;
    }
    setEditing({
      ...editing,
      stops: [
        ...(editing.stops || []),
        {
          label: `${o.customer_name || "Customer"}${o.order_number ? `, ${o.order_number}` : ""}`,
          address: addr,
          status: "pending",
          order_id: o.id,
          source: o.source,
        },
      ],
    });
    toast.success("Stop added.");
  };

  return (
    <div className="space-y-5" data-testid="manager-routes">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="font-label tracking-[0.4em] text-[11px] text-[var(--dahlias-clay)] uppercase">✦ Delivery Planning ✦</span>
          <h2 className="mt-1 font-display text-4xl text-[var(--dahlias-forest)]">Delivery routes</h2>
          <p className="text-sm text-[var(--dahlias-ink)]/65">Pull HQ orders into a route, reorder stops, then open the whole run in Google Maps.</p>
        </div>
        <button onClick={() => setEditing(blank())} data-testid="route-new" className="inline-flex items-center gap-1.5 h-10 px-4 wobble-pill bg-[var(--dahlias-forest)] hover:bg-[var(--dahlias-sage-deep)] text-[var(--dahlias-cream)] text-sm">
          <Plus className="w-4 h-4" /> New route
        </button>
      </div>

      {loading ? <div className="text-[var(--dahlias-ink)]/55">Loading…</div> : rows.length === 0 ? (
        <div className="bg-[var(--dahlias-paper)] border-2 border-dashed border-[var(--dahlias-sage)]/30 wobble-a p-10 text-center">
          <MapPin className="w-10 h-10 text-[var(--dahlias-sage)] mx-auto mb-3" />
          <p className="text-[var(--dahlias-ink)]/65">No delivery routes yet. Click &ldquo;New route&rdquo; to plan one.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const mapsUrl = buildMapsUrl(r.stops);
            return (
              <article key={r.id} data-testid={`route-${r.id}`} className="bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/25 wobble-a p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display text-2xl text-[var(--dahlias-forest)]">{r.name}</h3>
                      <span className={`px-2 py-0.5 wobble-pill text-[10px] tracking-[0.25em] uppercase ${r.active ? "bg-[var(--dahlias-sage-deep)] text-white" : "bg-[var(--dahlias-ink)]/10 text-[var(--dahlias-ink)]/65"}`}>{r.active ? "Active" : "Inactive"}</span>
                      <span className="px-2 py-0.5 wobble-pill text-[10px] tracking-[0.25em] uppercase bg-[var(--dahlias-clay)]/15 text-[var(--dahlias-clay)]">{(r.stops || []).length} stops</span>
                    </div>
                    {r.region && <div className="text-xs text-[var(--dahlias-ink)]/65 mt-0.5">{r.region}{r.scheduled_for ? ` · ${new Date(r.scheduled_for).toLocaleDateString("en-NZ")}` : ""}</div>}
                    {r.notes && <p className="mt-2 text-sm text-[var(--dahlias-ink)]/80 whitespace-pre-wrap">{r.notes}</p>}
                    {Array.isArray(r.stops) && r.stops.length > 0 && (
                      <ol className="mt-3 space-y-1.5">
                        {r.stops.map((s, i) => (
                          <li key={s.id || `${s.label || "stop"}-${i}`} className="flex items-start gap-3 text-sm">
                            <span className={`shrink-0 w-6 h-6 wobble-blob font-display grid place-items-center text-xs ${
                              s.status === "done"
                                ? "bg-[var(--dahlias-sage-deep)] text-white"
                                : "bg-[var(--dahlias-clay)]/15 text-[var(--dahlias-clay)]"
                            }`}>{s.status === "done" ? "✓" : i + 1}</span>
                            <div className="min-w-0 flex-1">
                              <div className={`text-[var(--dahlias-forest)] ${s.status === "done" ? "line-through opacity-60" : ""}`}>{s.label || "Stop"}</div>
                              {s.address && <div className="text-xs text-[var(--dahlias-ink)]/65 break-words">{s.address}</div>}
                            </div>
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {mapsUrl && (
                      <a
                        href={mapsUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        data-testid={`route-maps-${r.id}`}
                        title="Open this whole run in Google Maps"
                        className="inline-flex items-center gap-1.5 h-9 px-3 wobble-pill bg-[#1A73E8] hover:bg-[#0F5DC4] text-white text-sm shadow-sm"
                      >
                        <MapPin className="w-3.5 h-3.5" /> Navigate
                      </a>
                    )}
                    <button onClick={() => setEditing({ ...r })} data-testid={`route-edit-${r.id}`} className="inline-flex items-center gap-1.5 h-9 px-3 wobble-pill border border-[var(--dahlias-sage)]/30 text-sm hover:bg-[var(--dahlias-sage)]/10"><Edit2 className="w-3.5 h-3.5" /> Edit</button>
                    <button onClick={() => remove(r.id)} data-testid={`route-delete-${r.id}`} className="inline-flex items-center gap-1.5 h-9 px-3 wobble-pill bg-[var(--dahlias-rust)]/10 hover:bg-[var(--dahlias-rust)]/25 text-[var(--dahlias-rust)] text-sm"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {editing && (
        <div data-testid="route-editor" className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-5" onClick={() => setEditing(null)}>
          <div className="bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-clay)] wobble-a max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 shadow-2xl space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-2xl text-[var(--dahlias-forest)]">{editing.id ? "Edit route" : "New route"}</h3>
              <button onClick={() => setEditing(null)} className="p-1"><X className="w-5 h-5" /></button>
            </div>
            <Field label="Route name *"><input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="route-name" className="job-input" placeholder="e.g. Friday Auckland West" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Region"><input value={editing.region} onChange={(e) => setEditing({ ...editing, region: e.target.value })} className="job-input" placeholder="Waikato" /></Field>
              <Field label="Scheduled for"><input type="date" value={editing.scheduled_for ? editing.scheduled_for.slice(0,10) : ""} onChange={(e) => setEditing({ ...editing, scheduled_for: e.target.value })} className="job-input" /></Field>
            </div>
            <Field label="Notes"><textarea value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} rows={2} className="job-input min-h-[70px]" /></Field>

            <div>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-[10px] font-label tracking-[0.3em] uppercase text-[var(--dahlias-sage-deep)]">Stops (in order)</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowPicker((v) => !v)}
                    data-testid="route-toggle-picker"
                    className="inline-flex items-center gap-1 h-8 px-3 wobble-pill border border-[#6E1E3A]/40 text-xs text-[#6E1E3A] hover:bg-[#6E1E3A]/10"
                  >
                    <Package className="w-3.5 h-3.5" /> {showPicker ? "Hide" : "Pull"} HQ orders
                  </button>
                  <button onClick={addStop} type="button" data-testid="route-add-stop" className="inline-flex items-center gap-1 h-8 px-3 wobble-pill border border-[var(--dahlias-sage)]/30 text-xs hover:bg-[var(--dahlias-sage)]/10"><Plus className="w-3.5 h-3.5" /> Add blank</button>
                </div>
              </div>

              {showPicker && (
                <div data-testid="route-hq-picker" className="mt-3 bg-[var(--dahlias-cream)] border border-[#6E1E3A]/20 wobble-a p-3 max-h-56 overflow-y-auto">
                  {eligibleOrders.length === 0 ? (
                    <p className="text-xs text-[var(--dahlias-ink)]/55 italic">
                      No HQ orders yet, flag orders from the Admin → Orders page to bring them here.
                    </p>
                  ) : (
                    <ul className="divide-y divide-[#6E1E3A]/10">
                      {eligibleOrders.map((o) => {
                        const addr = formatStopAddress(o.address);
                        const used = (editing.stops || []).some((s) => s.order_id === o.id);
                        return (
                          <li key={`${o.source}-${o.id}`} className="py-2 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-[var(--dahlias-forest)]">
                                {o.customer_name || "Customer"}{o.order_number ? ` · ${o.order_number}` : ""}
                              </div>
                              <div className="text-xs text-[var(--dahlias-ink)]/65 truncate">{addr || "No address"}</div>
                            </div>
                            <button
                              type="button"
                              disabled={used || !addr}
                              onClick={() => addOrderAsStop(o)}
                              data-testid={`route-pick-${o.id}`}
                              className={`shrink-0 inline-flex items-center gap-1 h-8 px-3 wobble-pill text-xs ${
                                used
                                  ? "bg-[var(--dahlias-sage-deep)]/20 text-[var(--dahlias-sage-deep)] cursor-default"
                                  : addr
                                  ? "bg-[#6E1E3A] hover:bg-[#8a2548] text-[#FBE6D8]"
                                  : "bg-[var(--dahlias-ink)]/10 text-[var(--dahlias-ink)]/40 cursor-not-allowed"
                              }`}
                            >
                              {used ? "Added" : addr ? "Add" : "No address"}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}

              <div className="mt-3 space-y-2">
                {(editing.stops || []).map((s, idx) => (
                  <div key={s.id || `${s.label || "stop"}-${idx}`} className="grid grid-cols-12 gap-2 items-center">
                    <span className="col-span-1 w-7 h-7 wobble-blob bg-[var(--dahlias-clay)]/15 text-[var(--dahlias-clay)] grid place-items-center text-xs font-display">{idx + 1}</span>
                    <input value={s.label} placeholder="Label" onChange={(e) => updateStop(idx, { label: e.target.value })} className="col-span-3 job-input" />
                    <input value={s.address} placeholder="Address" onChange={(e) => updateStop(idx, { address: e.target.value })} className="col-span-5 job-input" />
                    <select
                      value={s.status || "pending"}
                      onChange={(e) => updateStop(idx, { status: e.target.value })}
                      data-testid={`route-stop-status-${idx}`}
                      className="col-span-2 job-input"
                    >
                      <option value="pending">Pending</option>
                      <option value="done">Done</option>
                    </select>
                    <div className="col-span-1 flex items-center justify-end gap-0.5">
                      <button onClick={() => moveStop(idx, -1)} type="button" data-testid={`route-stop-up-${idx}`} disabled={idx === 0} className="w-7 h-7 wobble-blob grid place-items-center text-[var(--dahlias-ink)]/65 hover:bg-[var(--dahlias-sage)]/10 disabled:opacity-30"><ChevronLeft className="w-4 h-4 rotate-90" /></button>
                      <button onClick={() => moveStop(idx, 1)} type="button" data-testid={`route-stop-down-${idx}`} disabled={idx === (editing.stops || []).length - 1} className="w-7 h-7 wobble-blob grid place-items-center text-[var(--dahlias-ink)]/65 hover:bg-[var(--dahlias-sage)]/10 disabled:opacity-30"><ChevronRight className="w-4 h-4 rotate-90" /></button>
                      <button onClick={() => removeStop(idx)} type="button" className="w-7 h-7 wobble-blob grid place-items-center text-[var(--dahlias-rust)] hover:bg-[var(--dahlias-rust)]/15"><X className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
                {(editing.stops || []).length === 0 && <div className="text-xs text-[var(--dahlias-ink)]/55 italic">No stops yet.</div>}
              </div>

              {(editing.stops || []).length >= 2 && buildMapsUrl(editing.stops) && (
                <a
                  href={buildMapsUrl(editing.stops)}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="route-preview-maps"
                  className="mt-3 inline-flex items-center gap-1.5 h-9 px-3 wobble-pill bg-[#1A73E8]/15 hover:bg-[#1A73E8]/25 text-[#1A73E8] text-sm"
                >
                  <MapPin className="w-3.5 h-3.5" /> Preview in Google Maps
                </a>
              )}
            </div>

            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="accent-[var(--dahlias-sage-deep)]" />
              Active route
            </label>

            <div className="flex justify-between pt-2">
              {editing.id ? (
                <button onClick={() => remove(editing.id)} type="button" className="inline-flex items-center gap-1.5 h-10 px-4 wobble-pill bg-[var(--dahlias-rust)]/15 hover:bg-[var(--dahlias-rust)]/25 text-[var(--dahlias-rust)] text-sm"><Trash2 className="w-4 h-4" /> Delete</button>
              ) : <span />}
              <button onClick={save} data-testid="route-save" className="inline-flex items-center gap-1.5 h-10 px-5 wobble-pill bg-[var(--dahlias-forest)] hover:bg-[var(--dahlias-sage-deep)] text-[var(--dahlias-cream)] text-sm"><Save className="w-4 h-4" /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* =================== Subscriptions =================== */

function SubscriptionsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const r = await mgrApi.get("/manager/subscriptions"); setData(r.data); }
      catch (e) { toast.error("Subscriptions load failed."); }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <div className="text-[var(--dahlias-ink)]/55">Loading…</div>;
  if (!data) return null;
  return (
    <div className="space-y-7" data-testid="manager-subscriptions">
      <div>
        <span className="font-label tracking-[0.4em] text-[11px] text-[var(--dahlias-clay)] uppercase">✦ Bloom Club ✦</span>
        <h2 className="mt-1 font-display text-4xl text-[var(--dahlias-forest)]">Subscriptions & waitlist</h2>
        <p className="text-sm text-[var(--dahlias-ink)]/65">{data.count} active · {data.waitlist_count} on waitlist.</p>
      </div>

      <section>
        <h3 className="font-label tracking-[0.3em] text-[11px] uppercase text-[var(--dahlias-clay)] mb-3">Active subscribers</h3>
        {data.subscribers.length === 0 ? <div className="text-[var(--dahlias-ink)]/55 italic">No active subscriptions yet.</div> : (
          <Table headers={[
            { label: "Customer" }, { label: "Region" }, { label: "Size · Vases · Freq" }, { label: "Status" }, { label: "Joined" },
          ]}>
            {data.subscribers.map((s) => (
              <tr key={s.id} className="border-t border-[var(--dahlias-sage)]/15 hover:bg-[var(--dahlias-sage)]/5">
                <td className="px-4 py-2.5 text-[var(--dahlias-forest)]">{s.name || s.first_name || s.email}</td>
                <td className="px-4 py-2.5 text-[var(--dahlias-ink)]/65 text-xs">{s.region || "-"}</td>
                <td className="px-4 py-2.5 text-[var(--dahlias-ink)]/70 text-xs">{[s.size, s.vase, s.frequency].filter(Boolean).join(" · ") || "-"}</td>
                <td className="px-4 py-2.5 text-xs"><span className="px-2 py-0.5 wobble-pill text-[10px] tracking-[0.2em] uppercase bg-[var(--dahlias-sage)]/15 text-[var(--dahlias-sage-deep)]">{s.status || "-"}</span></td>
                <td className="px-4 py-2.5 text-[var(--dahlias-ink)]/55 text-xs">{s.created_at ? new Date(s.created_at).toLocaleDateString("en-NZ") : "-"}</td>
              </tr>
            ))}
          </Table>
        )}
      </section>

      <section>
        <h3 className="font-label tracking-[0.3em] text-[11px] uppercase text-[var(--dahlias-clay)] mb-3">Waitlist</h3>
        {data.waitlist.length === 0 ? <div className="text-[var(--dahlias-ink)]/55 italic">No one on the waitlist yet.</div> : (
          <Table headers={[
            { label: "Name" }, { label: "Email" }, { label: "Region · Postcode" }, { label: "Joined" },
          ]}>
            {data.waitlist.map((w) => (
              <tr key={w.id || w.email} className="border-t border-[var(--dahlias-sage)]/15 hover:bg-[var(--dahlias-sage)]/5">
                <td className="px-4 py-2.5 text-[var(--dahlias-forest)]">{w.name || "-"}</td>
                <td className="px-4 py-2.5 text-[var(--dahlias-ink)]/70 font-mono text-xs">{w.email}</td>
                <td className="px-4 py-2.5 text-[var(--dahlias-ink)]/65 text-xs">{[w.region, w.postcode].filter(Boolean).join(" · ")}</td>
                <td className="px-4 py-2.5 text-[var(--dahlias-ink)]/55 text-xs">{w.created_at ? new Date(w.created_at).toLocaleDateString("en-NZ") : "-"}</td>
              </tr>
            ))}
          </Table>
        )}
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-label tracking-[0.3em] uppercase text-[var(--dahlias-sage-deep)] mb-1">{label}</span>
      {children}
    </label>
  );
}
